/**
 * THE AUTHOR STATE MACHINE, taken from the seeded rows and not from prose.
 *
 * `author_status` seeds FOUR rows and `author_transition_rule` seeds SIX. A move that is not a row
 * cannot be written: the composite foreign key `transition_is_legal` plus the
 * `author_state_event_is_legal` trigger refuse it before any application check runs. Both seeds
 * are copied verbatim below (04-schema.md, the `author_status` and `author_transition_rule`
 * INSERT blocks).
 *
 * WHY THE UI CARRIES THEM AT ALL, given that it can never perform a transition. Because they are
 * what makes the wording honest. Two facts fall straight out of the six rows and both of them are
 * things this feature says on screen:
 *   1. THERE IS NO `applicant -> disabled` ROW AND NO TERMINAL REFUSED STATE. An applicant who is
 *      asked for more information may re-apply for ever, and their status never moves backwards.
 *      So this feature has no rejection screen, and says "we would like to see more" rather than
 *      anything that implies a door has closed.
 *   2. THE ONLY MOVE AN AUTHOR THEMSELVES MAKES IS `author_start`. Approval, disabling and
 *      re-enabling are all `admin` or `editor`. So the only action this feature ever offers is
 *      submit or resubmit, and everything else is waiting.
 *
 * `new` is a PSEUDO-STATE: `is_author_state = false`, and `AuthorStatus` on the wire is
 * [applicant, approved, disabled] with no `new` value at all.
 */

/** The four rows of `author_status`, with the powers each carries as columns. */
export interface AuthorStatusRow {
  readonly code: 'new' | 'applicant' | 'approved' | 'disabled';
  readonly isAuthorState: boolean;
  readonly hasAuthorCapability: boolean;
  readonly inPublicProjection: boolean;
  readonly inDirectory: boolean;
  readonly labelEn: string;
}

export const AUTHOR_STATUS_ROWS: readonly AuthorStatusRow[] = [
  {
    code: 'new',
    isAuthorState: false,
    hasAuthorCapability: false,
    inPublicProjection: false,
    inDirectory: false,
    labelEn: 'Not an author',
  },
  {
    code: 'applicant',
    isAuthorState: true,
    hasAuthorCapability: false,
    inPublicProjection: false,
    inDirectory: false,
    labelEn: 'Applicant',
  },
  {
    code: 'approved',
    isAuthorState: true,
    // The ONLY row with the capability. It is never a Firebase custom claim: a claim lags up to
    // an hour behind a disable, and the disable is meant to bite at once.
    hasAuthorCapability: true,
    inPublicProjection: true,
    inDirectory: true,
    labelEn: 'Approved',
  },
  {
    code: 'disabled',
    isAuthorState: true,
    hasAuthorCapability: false,
    // True on purpose: a disabled author's published articles stay up, so their page still renders.
    inPublicProjection: true,
    inDirectory: false,
    labelEn: 'Disabled',
  },
];

/** One row of `author_transition_rule`. */
export interface AuthorTransitionRule {
  readonly fromStatus: AuthorStatusRow['code'];
  readonly toStatus: AuthorStatusRow['code'];
  readonly actorRole: 'author' | 'admin' | 'editor' | 'system_migration';
  readonly ruleKey: string;
  readonly requiresReason: boolean;
  readonly stampsApproval: boolean;
  readonly clearsDisabledStamp: boolean;
  readonly offersUnpublishAll: boolean;
  readonly blocksPayouts: boolean;
}

/** Six rows. The sixth is migration-only and grandfathers the ~530 existing authors. */
export const AUTHOR_TRANSITION_RULES: readonly AuthorTransitionRule[] = [
  {
    fromStatus: 'new',
    toStatus: 'applicant',
    actorRole: 'author',
    ruleKey: 'author_start',
    requiresReason: false,
    stampsApproval: false,
    clearsDisabledStamp: false,
    offersUnpublishAll: false,
    blocksPayouts: false,
  },
  {
    fromStatus: 'applicant',
    toStatus: 'approved',
    actorRole: 'admin',
    ruleKey: 'admin_approve',
    requiresReason: false,
    stampsApproval: true,
    clearsDisabledStamp: false,
    offersUnpublishAll: false,
    blocksPayouts: false,
  },
  {
    fromStatus: 'applicant',
    toStatus: 'approved',
    actorRole: 'editor',
    ruleKey: 'editor_approve',
    requiresReason: false,
    stampsApproval: true,
    clearsDisabledStamp: false,
    offersUnpublishAll: false,
    blocksPayouts: false,
  },
  {
    fromStatus: 'approved',
    toStatus: 'disabled',
    actorRole: 'admin',
    ruleKey: 'admin_disable',
    requiresReason: true,
    stampsApproval: false,
    clearsDisabledStamp: false,
    offersUnpublishAll: true,
    blocksPayouts: true,
  },
  {
    fromStatus: 'disabled',
    toStatus: 'approved',
    actorRole: 'admin',
    ruleKey: 'admin_reenable',
    requiresReason: true,
    stampsApproval: false,
    clearsDisabledStamp: true,
    offersUnpublishAll: false,
    blocksPayouts: false,
  },
  {
    fromStatus: 'new',
    toStatus: 'approved',
    actorRole: 'system_migration',
    ruleKey: 'migration_grandfather',
    requiresReason: false,
    stampsApproval: true,
    clearsDisabledStamp: false,
    offersUnpublishAll: false,
    blocksPayouts: false,
  },
];

/**
 * True only when a rule row exists. Nothing in the cabinet performs a transition — this is here so
 * a wording or a screen that assumes a move can happen is contradicted by data rather than by
 * somebody's memory.
 */
export function transitionIsLegal(
  from: AuthorStatusRow['code'],
  to: AuthorStatusRow['code'],
  actor: AuthorTransitionRule['actorRole'],
): boolean {
  return AUTHOR_TRANSITION_RULES.some(
    (rule) => rule.fromStatus === from && rule.toStatus === to && rule.actorRole === actor,
  );
}

/**
 * There is no row that moves an applicant anywhere except forward to `approved`. Asserted as a
 * derived value rather than written as a comment, so that adding a refusal row would flip it.
 */
export const APPLICANT_CAN_BE_REFUSED_TERMINALLY = AUTHOR_TRANSITION_RULES.some(
  (rule) => rule.fromStatus === 'applicant' && rule.toStatus === 'disabled',
);
